import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Paper, Typography, Chip } from '@mui/material';
import { Loop as LoopIcon } from '@mui/icons-material';
import { WorkflowNode } from '../../../../types/workflow.types';

const LoopNode: React.FC<NodeProps<WorkflowNode['data']>> = ({ data, selected }) => {
  const config = data?.config || {};

  const getLoopTypeColor = (loopType?: string) => {
    switch (loopType) {
      case 'for': return '#4CAF50';
      case 'while': return '#FF9800';
      case 'forEach': return '#9C27B0';
      default: return '#666666';
    }
  };

  const getLoopTypeIcon = (loopType?: string) => {
    switch (loopType) {
      case 'for': return '🔢';
      case 'while': return '⏳';
      case 'forEach': return '📋';
      default: return '🔄';
    }
  };

  return (
    <Paper
      elevation={selected ? 8 : 2}
      style={{
        padding: 16,
        minWidth: 150,
        maxWidth: 250,
        backgroundColor: '#607D8B',
        color: 'white',
        border: selected ? '3px solid #1976d2' : '1px solid #455A64',
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          backgroundColor: '#455A64',
          border: '2px solid white',
          width: 12,
          height: 12,
        }}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <LoopIcon style={{ fontSize: 20 }} />
        <Typography variant="subtitle2" fontWeight="bold">
          {data?.title || 'Loop'}
        </Typography>
      </div>

      {data?.description && (
        <Typography variant="caption" style={{ opacity: 0.9, fontSize: '0.7rem', marginBottom: 8, display: 'block' }}>
          {data?.description}
        </Typography>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
        {config?.loopType && (
          <Chip
            label={`${getLoopTypeIcon(config?.loopType)} ${config?.loopType?.toUpperCase() || ''}`}
            size="small"
            style={{
              backgroundColor: getLoopTypeColor(config?.loopType),
              color: 'white',
              fontSize: '0.6rem',
              height: 18,
            }}
          />
        )}

        {config?.iterations && (
          <Chip
            label={`${config?.iterations}x`}
            size="small"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontSize: '0.6rem',
              height: 16,
            }}
          />
        )}
      </div>

      {config?.condition && config?.loopType === 'while' && (
        <Typography
          variant="caption"
          style={{
            opacity: 0.8,
            fontSize: '0.65rem',
            fontFamily: 'monospace',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            padding: '2px 4px',
            borderRadius: '4px',
            display: 'block',
            marginBottom: 4,
            wordBreak: 'break-all'
          }}
        >
          While: {config?.condition && config.condition.length > 25 ? config.condition.substring(0, 25) + '...' : config?.condition}
        </Typography>
      )}

      {config?.breakCondition && (
        <Typography variant="caption" style={{ opacity: 0.7, fontSize: '0.6rem' }}>
          Break: {config?.breakCondition ? config.breakCondition.substring(0, 20) + '...' : ''}
        </Typography>
      )}

      {!config?.loopType && (
        <Typography variant="caption" style={{ opacity: 0.7, fontSize: '0.65rem' }}>
          Click to configure loop
        </Typography>
      )}

      {/* Loop body output handle (right) */}
      <Handle
        type="source"
        position={Position.Right}
        id="loop"
        style={{
          backgroundColor: '#455A64',
          border: '2px solid white',
          width: 12,
          height: 12,
          top: '50%',
        }}
      />

      {/* Continue/Exit output handle (bottom) */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="continue"
        style={{
          backgroundColor: '#455A64',
          border: '2px solid white',
          width: 12,
          height: 12,
        }}
      />
    </Paper>
  );
};

export default LoopNode;
